'use client';

/**
 * ProductTierRow — the shared presentational primitive behind the two buyer
 * product-tier explorer cards: SAVED (favourites_explorer) and WANTS
 * (custom_products_explorer).
 *
 * SAVED and WANTS are sibling buyer→product edges off the Actor, so both cards
 * are the SAME widget: a labelled, expandable row where the row click drives
 * the CANVAS and the chevron expands an inline product tree (the "row → canvas,
 * chevron → tree" split VendorRow uses). They differ only in four injected
 * props — label, icon, the product source, and the canvas target — so the shell
 * is factored here and each card is a thin wrapper (klyde-cards: "cards are
 * meant to be borrowed; a shared shell is factored into a primitive").
 *
 * Purely presentational: no data fetch, no BFF strategy, no capture export.
 * Each owning card fetches its own products (eager `saved_list` vs lazy
 * `vendor_explore`), owns its own `useCardExport`, and wires the canvas / active
 * / leaf behaviours. This component only paints. It owns exactly one piece of
 * state — whether the tree is expanded — and reports toggles via `onExpandChange`
 * so a lazy owner can gate its fetch on expansion.
 *
 * @see ./favourites-explorer-card.tsx — the SAVED wrapper
 * @see ./custom-products-explorer-card.tsx — the WANTS wrapper
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain explorer
 */

import { useState } from 'react';
import { ChevronRight, Loader2, Plus, type LucideIcon } from 'lucide-react';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { text, treeDepth as depthRamp, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

/** Normalised leaf — both SavedProductRow and ProductSummary map onto this. */
export interface ProductTierLeaf {
  id: string;
  name: string;
  list_price?: number | null;
  image_url?: string | null;
  /** delisted tombstone (favourites: active===false || sale_ok===false). */
  delisted?: boolean;
}

/** A group of leaves, shipped BY THE BFF (e.g. catalog_list_custom_products'
 *  category/type shelves). The frontend PAINTS it — it never computes grouping. */
export interface ProductTierGroup {
  label: string;
  products: ProductTierLeaf[];
}

interface ProductTierRowProps {
  label: string;
  icon: LucideIcon;
  iconClassName?: string;
  count: number;
  /** flat leaves (favourites) OR the ungrouped leftover under the groups. */
  products: ProductTierLeaf[];
  /** BFF-shipped groups, painted above the flat products. undefined = flat. */
  groups?: ProductTierGroup[];
  /** lazy owners: children still loading after expand. undefined = eager. */
  childrenLoading?: boolean;
  isActive: boolean;
  /** row click → canvas. Optional: with no handler the row toggles the tree. */
  onOpen?: () => void;
  /** notified on expand toggle so a lazy owner can trigger its fetch */
  onExpandChange?: (expanded: boolean) => void;
  onLeafClick: (leaf: ProductTierLeaf) => void;
  /** highlighted leaf id (e.g. favourites' inspected entity); null = none */
  activeLeafId?: string | null;
  /** optional per-leaf title/tooltip; defaults to the leaf name */
  leafTitle?: (leaf: ProductTierLeaf) => string;
  /** optional header "+" affordance (e.g. custom products' Add custom item) */
  onAdd?: () => void;
  addLabel?: string;
  /** optional per-leaf trailing slot (e.g. custom products' Edit/Remove kebab) */
  leafTrailing?: (leaf: ProductTierLeaf) => React.ReactNode;
}

export function ProductTierRow({
  label,
  icon: Icon,
  iconClassName,
  count,
  products,
  groups,
  childrenLoading,
  isActive,
  onOpen,
  onExpandChange,
  onLeafClick,
  activeLeafId,
  leafTitle,
  onAdd,
  addLabel,
  leafTrailing,
}: ProductTierRowProps) {
  const [expanded, setExpanded] = useState(false);
  const labelLoadingChildren = useProductLabel('vendor_loading_children');
  const depthClass = depthRamp[0];
  const leafClass = depthRamp[2];
  // Nothing to expand when empty (add-only tier) — no chevron, no count,
  // mirroring VendorRow's childless spacer.
  const hasChildren = count > 0;

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Compute next OUTSIDE the updater — the updater must stay pure. Calling
    // onExpandChange (a parent setState) inside it fires a cross-component
    // update during render. This is a click handler, so reading `expanded`
    // from the closure is correct.
    const next = !expanded;
    setExpanded(next);
    onExpandChange?.(next);
  };

  // Row-body click: an explicit onOpen (favourites → canvas) wins; with none
  // (custom products) the row just toggles the inline tree — no tab, so no
  // legacy mode-promotion side-effect.
  const handleRowClick = () => {
    if (onOpen) {
      onOpen();
      return;
    }
    const next = !expanded;
    setExpanded(next);
    onExpandChange?.(next);
  };

  // One leaf row — reused for both grouped and flat rendering. Wrapper is a div
  // (not a button) so an optional trailing kebab is a valid sibling.
  const renderLeaf = (p: ProductTierLeaf) => (
    <div
      key={p.id}
      className={cn(
        'group/leaf flex items-center gap-1 rounded pl-4 pr-1 hover:bg-accent/50',
        activeLeafId === p.id && 'bg-accent/60',
        p.delisted && 'opacity-50',
      )}
    >
      <button
        onClick={() => onLeafClick(p)}
        title={leafTitle ? leafTitle(p) : p.name}
        className="flex items-center gap-1 flex-1 min-w-0 py-1 text-left"
      >
        <span className={cn(leafClass, 'truncate flex-1', activeLeafId === p.id && 'text-primary')}>
          {p.name}
        </span>
        {p.list_price != null && (
          <span className={cn(text.muted, 'shrink-0')}>${p.list_price.toLocaleString()}</span>
        )}
      </button>
      {leafTrailing?.(p)}
    </div>
  );

  return (
    <div
      style={{ fontSize: surfaceBase.canvas }}
      className={cn(
        'relative rounded-lg border bg-card transition-colors',
        isActive ? 'border-primary/30 bg-primary/5' : 'border-border hover:bg-accent/30',
      )}
    >
      <div
        onClick={handleRowClick}
        className="group flex items-center gap-2 px-2 py-1.5 cursor-pointer"
      >
        {hasChildren ? (
          <button
            onClick={toggleExpand}
            className="p-0.5 hover:bg-muted rounded shrink-0"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronRight
              className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', expanded && 'rotate-90')}
            />
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <Icon className={cn('h-3.5 w-3.5 shrink-0', iconClassName)} />
        <span className={cn(depthClass, 'truncate flex-1', isActive && 'text-primary font-medium')}>
          {label}
        </span>
        {hasChildren && <span className={cn(text.muted, 'shrink-0')}>{count}</span>}
        {onAdd && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
            aria-label={addLabel}
            title={addLabel}
            className="p-0.5 rounded shrink-0 text-primary hover:bg-accent/50 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {expanded && hasChildren && (
        <div className="ml-3 mb-1 pl-1 border-l border-border/50">
          {childrenLoading ? (
            <div className={cn(text.muted, 'flex items-center gap-2 px-3 py-1')}>
              <Loader2 className="h-3 w-3 animate-spin" />
              {labelLoadingChildren}
            </div>
          ) : (
            <>
              {/* BFF-shipped groups, painted as-is: a category/type header per
                  group, its products beneath. The BFF decided the grouping. */}
              {groups?.map((g) => (
                <div key={g.label}>
                  <div className={cn(text.muted, 'px-2 py-0.5 pl-3 uppercase tracking-wide opacity-70')}>
                    {g.label}
                  </div>
                  {g.products.map(renderLeaf)}
                </div>
              ))}
              {/* Ungrouped leftover (or the whole flat list for favourites). */}
              {products.map(renderLeaf)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
